/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/tierHelpers.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing Queue tiers.

────────────────────────────────────────────────────────────────*/

import { TierRow } from '../types';

/**
 * Normalize tier rows for API submission.
 *
 * NOTE: agentId may be a Sqid-encoded string (e.g. "qdZW4AXl") because the
 * backend sqidsPlugin encodes all numeric ID fields in responses.
 * We pass agentId as a string directly — the backend decodeRequestBodyIds
 * hook will decode it transparently before the service sees it.
 * We only filter rows with an empty/missing agentId.
 */
export function normalizeTierRows(rows: TierRow[]) {
  return rows
    .map((row) => {
      // Accept both Sqid strings and plain numeric strings
      const rawAgentId = String(row.agentId ?? '').trim();
      if (!rawAgentId) {
        return null;  // No agent selected → skip this tier row
      }

      const level = Number(row.level);
      const position = Number(row.position);

      const tierData: {
        agentId: string;  // Sent as string; backend Sqids plugin decodes it
        level: number;
        position: number;
        id?: string;  // Also a Sqid in edit mode; backend plugin decodes it
      } = {
        agentId: rawAgentId,
        level: Number.isFinite(level) && level >= 1 ? Math.trunc(level) : 1,
        position: Number.isFinite(position) && position >= 1 ? Math.trunc(position) : 1
      };

      // Only include id if it exists (update mode — also a Sqid)
      if (row.id !== undefined && row.id !== null) {
        const rawId = String(row.id).trim();
        if (rawId && rawId !== '0') {
          tierData.id = rawId;
        }
      }

      return tierData;
    })
    .filter(
      (
        tier
      ): tier is {
        agentId: string;
        level: number;
        position: number;
        id?: string;
      } => tier !== null
    );
}

